import moment from "moment-timezone"
import { BALLS_TBD } from "pages/EventsManagement/eventCreationForm"
import { countryTimezone, seasonOfDay } from "./plannerQuery"

// The proposal editor's form (Phase A6 fields, without fees, WildCards and per-competition start times; ticket 08). The editor
// picks days; the proposal stores instants in the country's time zone. A hand-made proposal starts at 09:00 and ends at 18:00; a
// changed day keeps the time it had, as generation keeps the previous edition's (integrator, 2026-10-21).
export const DEFAULT_START_TIME = "09:00"
export const DEFAULT_END_TIME = "18:00"
// B3 pinned decision Q6: a proposal lasts one day at least and 120 days at most, counted as calendar days of its country.
export const MAX_PROPOSAL_DAYS = 120

const DAY = "YYYY-MM-DD"
const dayOf = (instant, timeZone) => moment(instant).tz(timeZone).format(DAY)
const timeOf = (instant, timeZone) => moment(instant).tz(timeZone).format("HH:mm")
const validDay = value => moment.utc(value, DAY, true).isValid()
export const instantOf = (day, time, timeZone) => moment.tz(`${day}T${time}`, "YYYY-MM-DDTHH:mm", true, timeZone).toISOString()
export const proposalDays = ({ startDay, endDay }) => moment.utc(endDay, DAY).diff(moment.utc(startDay, DAY), "days") + 1

// The country of a proposal: its club's, or the one chosen for TBA and No club.
export const clubCountry = club => (club?.kind === "club" ? club.location?.country || null : null)
const text = value => (value === null || value === undefined ? "" : String(value))
const competitionForm = competition => ({ key: competition.id, id: competition.id, level: text(competition.level), gameType: text(competition.gameType),
  drawModel: text(competition.drawModel), mlMaxNumber: text(competition.mlMaxNumber), stored: competition })

export const formOf = (proposal, organizerName = null) => {
  const timeZone = countryTimezone(proposal.country)
  return {
    name: text(proposal.name), checkName: Boolean(proposal.checkName), sportType: proposal.sportType, category: proposal.category, playMode: text(proposal.playMode), country: proposal.country,
    startDay: dayOf(proposal.startDate, timeZone), endDay: dayOf(proposal.endDate, timeZone),
    startTime: timeOf(proposal.startDate, timeZone), endTime: timeOf(proposal.endDate, timeZone),
    club: proposal.club, organizer: proposal.organizer ? { id: proposal.organizer.id, label: organizerName } : null,
    coordinator: text(proposal.coordinator?.name), officialBall: proposal.officialBall ? proposal.officialBall.tbd ? BALLS_TBD : proposal.officialBall.name : "",
    courtType: text(proposal.courtType), numberOfCourts: text(proposal.numberOfCourts), singlesMaxNumber: text(proposal.singlesMaxNumber),
    doublesMaxNumber: text(proposal.doublesMaxNumber), directorName: text(proposal.directorName), refereeName: text(proposal.refereeName),
    supervisorName: text(proposal.supervisorName), competitions: (proposal.competitions || []).map(competitionForm),
    previousEdition: proposal.previousEdition || null,
  }
}
// A new proposal: the view's first sport and its country, the default times, and the days of the calendar's day clicked (if any);
// everything else is chosen.
export const newForm = (query, days) => ({
  name: "", checkName: false, sportType: query.sports[0], category: "", playMode: "", country: query.country, startDay: days?.start || "", endDay: days?.end || "",
  startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME, club: null, organizer: null, coordinator: "", officialBall: "", courtType: "",
  numberOfCourts: "", singlesMaxNumber: "", doublesMaxNumber: "", directorName: "", refereeName: "", supervisorName: "", competitions: [],
  previousEdition: null,
})
let newKeys = 0
export const newCompetition = () => ({ key: "new-" + (newKeys += 1), level: "", gameType: "", drawModel: "", mlMaxNumber: "" })

const whole = value => /^\d+$/.test(String(value).trim())
// What the form refuses before sending: the Ciornă fields (sport, category, dates, country), the dates in order and at most 120 days,
// and whole numbers where numbers go. Everything else may stay empty in a draft.
export const formErrors = values => {
  const errors = {}
  if (!values.sportType) errors.sportType = "Choose a sport."
  if (!values.category) errors.category = "Choose a category."
  if (!validDay(values.startDay)) errors.startDay = "Choose the start date."
  if (!validDay(values.endDay)) errors.endDay = "Choose the end date."
  if (!errors.startDay && !errors.endDay) {
    if (values.endDay < values.startDay) errors.endDay = "The end cannot be before the start."
    else if (proposalDays(values) > MAX_PROPOSAL_DAYS) errors.endDay = `A proposal lasts at most ${MAX_PROPOSAL_DAYS} days; this one lasts ${proposalDays(values)}.`
    else if (values.endDay === values.startDay && values.endTime <= values.startTime) errors.endDay = "On a one-day proposal the end must come after the start."
  }
  if (!(clubCountry(values.club) || values.country)) errors.country = "Choose the country."
  if (values.numberOfCourts !== "" && !whole(values.numberOfCourts)) errors.numberOfCourts = "Enter a whole number of courts, 0 or more."
  values.competitions.forEach(competition => {
    if (competition.mlMaxNumber !== "" && (!whole(competition.mlMaxNumber) || Number(competition.mlMaxNumber) < 1)) {
      errors["competitions." + competition.key] = "Main List capacity is a whole number, at least 1."
    }
  })
  return errors
}

const orNull = value => (String(value).trim() ? String(value).trim() : null)
const numberOrNull = value => (String(value).trim() === "" ? null : Number(value))
const clubReference = club => (!club ? null : club.kind === "club" ? { kind: "club", id: club.id } : { kind: club.kind })
// The proposal fields the form holds, as the commands take them (Phase A6). A competition keeps what the editor does not show (its
// groups, restrictions, prizes and balls per match), so an edit never drops them.
export const fieldsOf = values => {
  const country = clubCountry(values.club) || values.country
  const timeZone = countryTimezone(country)
  return {
    name: orNull(values.name), checkName: Boolean(values.checkName), sportType: values.sportType, category: values.category, playMode: values.playMode || null, country,
    startDate: instantOf(values.startDay, values.startTime, timeZone), endDate: instantOf(values.endDay, values.endTime, timeZone),
    season: seasonOfDay(values.startDay), club: clubReference(values.club), organizer: values.organizer ? { id: values.organizer.id } : null,
    coordinator: values.coordinator ? { name: values.coordinator } : null,
    officialBall: values.officialBall === BALLS_TBD ? { tbd: true } : values.officialBall ? { name: values.officialBall } : null,
    courtType: values.courtType || null, numberOfCourts: numberOrNull(values.numberOfCourts), singlesMaxNumber: numberOrNull(values.singlesMaxNumber),
    doublesMaxNumber: numberOrNull(values.doublesMaxNumber), directorName: orNull(values.directorName), refereeName: orNull(values.refereeName),
    supervisorName: orNull(values.supervisorName),
    competitions: values.competitions.map(({ stored, level, gameType, drawModel, mlMaxNumber }) => ({ ...(stored || {}), level: numberOrNull(level),
      gameType: gameType || null, drawModel: drawModel || null, mlMaxNumber: numberOrNull(mlMaxNumber) })),
    previousEdition: values.previousEdition || null,
  }
}
// A new draft: its fields without the empty ones (the command's defaults fill them; "Check the name" is off by default).
export const draftOf = values => Object.fromEntries(Object.entries(fieldsOf(values)).filter(([, value]) => value !== null && value !== false
  && !(Array.isArray(value) && !value.length)))
// An edit: only the fields that changed since the proposal was read.
export const changesOf = (proposal, values) => {
  const before = fieldsOf(formOf(proposal)), after = fieldsOf(values)
  return Object.fromEntries(Object.keys(after).filter(field => JSON.stringify(before[field]) !== JSON.stringify(after[field]))
    .map(field => [field, after[field]]))
}
// „Propose competitions” (ticket 11, D3): a line the editor adds with its id and its source, kept until it is saved.
export const proposedCompetition = line => ({ key: line.id, id: line.id, level: text(line.level), gameType: text(line.gameType), drawModel: text(line.drawModel),
  mlMaxNumber: text(line.mlMaxNumber), stored: { id: line.id, numberOfGroups: line.numberOfGroups ?? null }, source: line.source })
// The sources of the competitions the form adds ("competitions.<id>"), which the create or edit command stores with them.
export const addedSources = values => Object.fromEntries(values.competitions.filter(({ source, id }) => source && id).map(({ id, source }) => [`competitions.${id}`, source]))
// The editor reads (D3) take the editor's values as they stand, saved or not: the place in the calendar, the dates, the competitions held.
export const editorQuery = (values, extra = {}) => {
  const country = clubCountry(values.club) || values.country
  const timeZone = countryTimezone(country)
  const held = values.competitions.filter(({ level, gameType }) => level !== "" && gameType).map(({ level, gameType }) => `${gameType}:${level}`)
  return new URLSearchParams(Object.entries({ country, sportType: values.sportType, category: values.category, playMode: values.playMode,
    clubId: values.club?.kind === "club" ? values.club.id : "", organizerId: values.organizer?.id || "",
    startDate: validDay(values.startDay) ? instantOf(values.startDay, values.startTime, timeZone) : "",
    endDate: validDay(values.endDay) ? instantOf(values.endDay, values.endTime, timeZone) : "", competitions: held.join(","), ...extra })
    .filter(([, value]) => value))
}

// What the organizer confirms (ticket 16): a change of one of them on a Confirmată returns it to Propusă.
export const CONFIRMED_FIELDS = ["startDate", "endDate", "club", "organizer", "category", "playMode", "sportType"]
